import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, NavLink } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaBars, FaHome, FaCalendar, FaUsers, FaCog, FaSignOutAlt, FaDesktop } from 'react-icons/fa';
import * as FaIcons from 'react-icons/fa';
import { AuthContext } from '../../../context/AuthContext';
import ProfileImage from '../../../components/ProfileImage';
import desktopService from '../../../services/desktopService';
import userPanelPreferenceService from '../../../services/userPanelPreferenceService';
import SettingsService from '../../../services/SettingsService';
import { API_BASE_URL } from '../../../configs/apiConfig';
import { menuConfig } from "../../../configs/menuConfig";
import Spinner from "../../../components/Spinner";
import '../styles/UserLeftMenu.css';
import '../styles/Tooltip.css';

// Component that only shows tooltip when text is truncated
const ConditionalTooltip = ({ children, tooltipText, placement = "right", tooltipId }) => {
    const textRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
        const checkTruncation = () => {
            if (textRef.current) {
                const element = textRef.current;
                const isOverflowing = element.scrollWidth > element.clientWidth;
                setShowTooltip(isOverflowing);
            }
        };

        checkTruncation();
        
        // Check again on window resize
        window.addEventListener('resize', checkTruncation);
        return () => window.removeEventListener('resize', checkTruncation);
    }, [tooltipText]);

    // Clone the children element and add ref
    const childrenWithRef = React.cloneElement(children, { ref: textRef });

    if (showTooltip) {
        return (
            <OverlayTrigger
                placement={placement}
                overlay={<Tooltip id={tooltipId}>{tooltipText}</Tooltip>}
            >
                {childrenWithRef}
            </OverlayTrigger>
        );
    }

    return childrenWithRef;
};

const UserLeftMenu = ({ 
    isCollapsed, 
    onToggle,
    darkMode,
    opacity,
    blur,
    baseColor,
    desktops = [], 
    currentDesktop,
    onDesktopSelect,
    onProfileClick
}) => {
    const { user } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [organizationLogo, setOrganizationLogo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const loadOrganizationLogo = async () => {
            try {
                const settings = await SettingsService.getSettings();
                if (settings?.organizationLogo) {
                    const logoPath = settings.organizationLogo;
                    // If the path is relative, prepend the API base URL
                    const logoUrl = logoPath.startsWith('http') 
                        ? logoPath 
                        : `${API_BASE_URL}${logoPath}`;
                    setOrganizationLogo(logoUrl);
                }
            } catch (error) {
                console.error('Error loading organization logo:', error);
            }
        };
        
        loadOrganizationLogo();
    }, []);

    const handleDesktopClick = async (desktop) => {
        try {
            // Call the parent's onDesktopSelect without showing loading in menu
            await onDesktopSelect(desktop);
            
            // Let UserDashboard handle both external and internal links
            // We no longer need to navigate here
        } catch (error) {
            console.error('Error handling desktop click:', error);
        }
    };

    // Convert hex to RGB for background tinting
    const hexToRgb = (hex) => {
        if (!hex) return null;
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
        
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };
    
    const getBackgroundColor = () => {
        if (!baseColor) {
            return null; // Let CSS handle the default
        }
        
        // Convert base color to RGB
        const rgb = hexToRgb(baseColor);
        if (!rgb) return null;
        
        if (darkMode) {
            // Dark mode: Use the actual base color with opacity
            return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
        } else {
            // Light mode: Use a lighter tint of the base color
            // Mix with white to create a pleasant light version
            const lightFactor = 0.35; // Keep 35% of the original color
            const lightR = Math.round(rgb.r * lightFactor + 255 * (1 - lightFactor));
            const lightG = Math.round(rgb.g * lightFactor + 255 * (1 - lightFactor));
            const lightB = Math.round(rgb.b * lightFactor + 255 * (1 - lightFactor));
            return `rgba(${lightR}, ${lightG}, ${lightB}, ${opacity})`;
        }
    };

    // Add effect to update CSS variables when props change
    useEffect(() => {
        const bgColor = getBackgroundColor();
        if (bgColor) {
            document.documentElement.style.setProperty('--menu-bg-color', bgColor);
        } else {
            document.documentElement.style.removeProperty('--menu-bg-color');
        }
    }, [baseColor, darkMode, opacity]);

    // Get user role text
    const getUserType = () => {
        if (!user) return "User";
        switch(user.userType) {
            case 0: return "Administrator";
            case 1: return "Normal User";
            case 2: return "Manager";
            default: return "User";
        }
    };

    // Get first 3 positions
    const getPositions = () => {
        if (!user?.positions) return [];
        return user.positions.slice(0, 3).map(pos => pos.positionTitle);
    };

    const getDesktopPath = (desktop) => {
        if (desktop.url) {
            return desktop.url;
        }
        if (desktop.pageCode) {
            return menuConfig[desktop.pageCode] || "/";
        }
        return "/user-dashboard/";
    };

    const getIconComponent = (desktop) => {
        if (desktop.iconImage) {
            return (
                <img 
                    src={desktop.iconImage.startsWith('http') ? desktop.iconImage : `${API_BASE_URL}${desktop.iconImage}`}
                    alt={desktop.name}
                    className="desktop-icon-image"
                />
            );
        }
        
        // Try to get the icon from react-icons/fa
        if (desktop.iconCode) {
            const iconName = `Fa${desktop.iconCode}`;
            const IconComponent = FaIcons[iconName];
            if (IconComponent) {
                return <IconComponent className="desktop-icon" />;
            }
        }
        
        // Fallback to default desktop icon
        return <FaDesktop className="desktop-icon" />;
    };

    const menuItems = desktops.map(desktop => ({
        path: getDesktopPath(desktop),
        icon: getIconComponent(desktop),
        label: desktop.name,
        desktop: desktop
    }));

    return (
        <>
            <div className="mobile-menu-toggle">
                <button onClick={onToggle}>
                    <FaBars />
                </button>
            </div>
            <div className={`user-dashboard-left-menu ${isCollapsed ? 'collapsed' : ''} ${darkMode ? 'dark-mode' : ''}`}>
                <button className="user-dashboard-menu-toggle" onClick={onToggle}>
                    <FaBars />
                </button>
                
                {/* User Profile Section */}
                <div className={`user-dashboard-profile ${isCollapsed ? 'collapsed' : ''}`}>
                    <div 
                        className="user-dashboard-avatar"
                        onClick={onProfileClick}
                        style={{ cursor: 'pointer' }}
                    >
                        <ProfileImage 
                            imageUrl={user?.imageUrl || ""}
                            width={60}
                            height={60}
                        />
                    </div>
                    {!isCollapsed && (
                        <div className="user-dashboard-info">
                            <ConditionalTooltip
                                tooltipText={user?.username || "User"}
                                tooltipId="username-tooltip"
                            >
                                <h4 className="user-dashboard-name">{user?.username || "User"}</h4>
                            </ConditionalTooltip>
                            <ConditionalTooltip
                                tooltipText={getUserType()}
                                tooltipId="usertype-tooltip"
                            >
                                <p className="user-dashboard-type">{getUserType()}</p>
                            </ConditionalTooltip>
                            <div className="user-dashboard-positions">
                                {getPositions().map((position, index) => (
                                    <ConditionalTooltip
                                        key={index}
                                        tooltipText={position}
                                        tooltipId={`position-tooltip-${index}`}
                                    >
                                        <p className="user-dashboard-position">{position}</p>
                                    </ConditionalTooltip>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Navigation Items */}
                <div className="user-dashboard-menu-items">
                    {menuItems.map((item, index) => (
                        <div
                            key={index}
                            className={`user-dashboard-menu-item ${currentDesktop?.id === item.desktop.id ? 'active' : ''}`}
                            onClick={() => handleDesktopClick(item.desktop)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="user-dashboard-menu-icon-wrapper">
                                {item.icon}
                            </div>
                            {!isCollapsed && (
                                <ConditionalTooltip
                                    tooltipText={item.label}
                                    tooltipId={`menu-tooltip-${index}`}
                                >
                                    <span className="user-dashboard-menu-label">
                                        {item.label}
                                    </span>
                                </ConditionalTooltip>
                            )}
                        </div>
                    ))}
                </div>

                {/* Company Logo and Logout */}
                <div className="user-dashboard-footer">
                    <div className="user-dashboard-company-logo">
                        <a 
                            href="https://fec.ai" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ display: 'block' }}
                        >
                            <img 
                                src={organizationLogo || "/logo.png"} 
                                alt="Organization Logo" 
                                style={{ cursor: 'pointer' }}
                            />
                        </a>
                    </div>
                    <Link to="/logout" className="user-dashboard-menu-item logout">
                        <div className="user-dashboard-menu-icon-wrapper">
                            <FaSignOutAlt className="user-dashboard-menu-icon" />
                            {isCollapsed && <span className="user-dashboard-menu-label">Logout</span>}
                        </div>
                        {!isCollapsed && <span className="user-dashboard-menu-label">Logout</span>}
                    </Link>
                </div>
            </div>
        </>
    );
};

export default UserLeftMenu; 